import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { EncryptionService } from '../services/encryption.service';
import { ToastService } from '../services/toast.service';

/**
 * Error Interceptor
 *
 * Intercepts all HTTP error responses and:
 * - 401: Logs out and redirects to login
 * - Invalid encryption key: clears the stale key and prompts unlock
 * - 422: Shows validation error messages from backend via toast
 * - Other errors: Shows the backend message via toast
 * - display.value === 'never': Suppresses the toast entirely
 *
 * Backend ErrorResponse contract (from api.yaml):
 * {
 *   message: string;
 *   errors: Record<string, string[]> | [];
 * }
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const encryptionService = inject(EncryptionService);
  const router = inject(Router);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError(error => {
      // Handle 401 Unauthorized responses
      if (error.status === 401) {
        authService.logout();
        router.navigate(['/login']);
        return throwError(() => error);
      }

      // Stale/rotated encryption key — clear it and ask the user to re-enter.
      if (isInvalidEncryptionKeyError(error)) {
        void encryptionService.clearInvalidKey();
        toastService.showError(
          'Your encryption key is outdated. Please enter the new key.'
        );
        return throwError(() => error);
      }

      // Skip toast when backend explicitly opts out via display: "never"
      const display = error.error?.display?.value;
      if (display === 'never') {
        return throwError(() => error);
      }

      // Extract error message from backend response
      const errorMessage = extractErrorMessage(error);

      if (errorMessage) {
        toastService.showError(errorMessage);
      }

      // Re-throw the error to be handled by the calling service
      return throwError(() => error);
    })
  );
};

export function isInvalidEncryptionKeyError(error: HttpErrorResponse): boolean {
  const message = error.error?.message;
  if (typeof message !== 'string' || message.trim() === '') {
    return false;
  }

  return (
    message.includes('Invalid encryption key') ||
    message.includes('Decrypted key does not match stored hash')
  );
}

/**
 * Extract a user-friendly error message from the HTTP error response.
 *
 * Follows the backend ErrorResponse contract:
 * - `message`: General error description
 * - `errors`: Validation errors keyed by field name, or empty array
 *
 * For validation errors (422), concatenates field-specific messages.
 * For other errors, uses the general `message` field.
 */
function extractErrorMessage(error: {
  status: number;
  error?: { message?: string; errors?: Record<string, string[]> | [] };
}): string {
  const body = error.error;
  if (!body) return '';

  // Use the general message from backend
  const generalMessage = body.message || '';

  // For validation errors, append field-specific messages
  if (error.status === 422 && body.errors && !Array.isArray(body.errors)) {
    const fieldErrors = Object.values(body.errors).flat();
    if (fieldErrors.length > 0) {
      return fieldErrors.join('. ');
    }
  }

  return generalMessage;
}
